// src/components/ImageSlider.js
import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, A11y } from 'swiper';
import 'swiper/swiper.min.css';
import 'swiper/components/navigation/navigation.min.css';
SwiperCore.use([Navigation, A11y]);
import Image from 'next/image';
import { useBreakpoints } from '@hooks/use-breakpoints';
import { MemberProps } from '@root/config/the-team';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';

interface ImageSliderProps {
    images: MemberProps[];
    slidesToShow: number;
    setCurrentIndex: (index: number) => void;
    initialSlide: number;
}

const ImageSlider: React.FC<ImageSliderProps> = props => {
    const [imageWidth, setImageWidth] = useState(100);
    const [imageHeight, setImageHeight] = useState(100);
    const [space, setSpace] = useState(-50);
    const { isMd, isLg, isXl, width } = useBreakpoints();
    const ref1 = useRef(null);
    const ref2 = useRef(null);

    const settings = {
        centeredSlides: true,
        loop: true,
        initialSlide: props.initialSlide,
        spaceBetween: space,
        onActiveIndexChange: key => {
            if (key.activeIndex === 6) {
                props.setCurrentIndex(0);
            } else if (key.activeIndex === 2) {
                props.setCurrentIndex(2);
            } else if (key.activeIndex) {
                props.setCurrentIndex(key.activeIndex - 3);
            }
        },
        breakpoints: {
            160: {
                slidesPerView: props.slidesToShow,
            },
        },
    };

    const templateImages = props.images.map((image, key) => {
        return (
            <React.Fragment key={image.alt}>
                <SwiperSlide key={key}>
                    <div className="slideWrapper">
                        <Image
                            objectFit="cover"
                            layout="intrinsic"
                            width={imageWidth}
                            height={imageHeight}
                            src={image.src}
                            alt={image.alt}
                        />
                    </div>
                </SwiperSlide>
            </React.Fragment>
        );
    });

    useEffect(() => {
        setImageHeight(100);
        setImageWidth(100);
        setSpace(-25);

        if (isMd) {
            setImageHeight(200);
            setImageWidth(200);
            setSpace(-50);
        }

        if (isLg) {
            setImageHeight(225);
            setImageWidth(225);
        }

        if (isXl) {
            setImageHeight(250);
            setImageWidth(250);
        }
    }, [width]);

    return (
        <React.Fragment>
            <span
                ref={ref1}
                className="absolute hidden lg:inline z-10 text-tan left-8 top-[20rem] cursor-pointer opacity-40 hover:opacity-100"
            >
                <FontAwesomeIcon size="4x" icon={faCaretLeft} />
            </span>
            <span
                ref={ref2}
                className="absolute hidden lg:inline z-10 text-tan right-8 top-[20rem] cursor-pointer opacity-40 hover:opacity-100"
            >
                <FontAwesomeIcon size="4x" icon={faCaretRight} />
            </span>
            <Swiper {...settings} navigation={{ prevEl: ref1.current, nextEl: ref2.current }}>
                {templateImages}
            </Swiper>
        </React.Fragment>
    );
};

export default ImageSlider;
